/**
 * Tag vocabulary and span helpers.
 *
 * `unverifiable` is deliberately absent: it was removed from the human palette. The
 * backend enforces the same three labels with a CHECK constraint, so the UI can never
 * offer something the database will reject.
 */

export const SPAN_TYPES = ['hallucinated', 'uncertain', 'wrong_uncertainty'];

export const TAG_META = {
  hallucinated: {
    label: 'Hallucination',
    short: 'H',
    hotkey: 'h',
    className: 'ann-hallucination',
    help: 'The image does not show this.',
  },
  uncertain: {
    label: 'Uncertain',
    short: 'U',
    hotkey: 'u',
    className: 'ann-uncertain',
    help: 'Probably right, but the image cannot confirm it.',
  },
  wrong_uncertainty: {
    label: 'Wrong uncertainty',
    short: 'W',
    hotkey: 'w',
    className: 'ann-wrong',
    help: 'The caption hedges about something the image makes plain.',
  },
};

/**
 * Element name used in `human_marked_caption_with_edits`.
 *
 * Note `hallucinated` -> `<hallucination>`. That asymmetry is inherited from the legacy
 * export and is load-bearing: downstream analysis parses these tags, so changing them
 * would silently break every script that reads the research artefact.
 */
export const MARKUP_TAG = {
  hallucinated: 'hallucination',
  uncertain: 'uncertain',
  wrong_uncertainty: 'wrong_uncertainty',
};

export const markupTagOf = (span) => MARKUP_TAG[labelOf(span)] || 'span';

export const OPERATIONS = {
  remove: { label: 'Remove', hint: 'Delete this text from the final caption' },
  replace: { label: 'Replace', hint: 'Swap this text for a correction' },
  keep_with_uncertainty_note: {
    label: 'Keep + note',
    hint: 'Leave the words, record what is unclear',
  },
};

/** Uncertain spans keep their text and carry a note; the others are wrong, so default
 *  to removing them. Matches the legacy behaviour and the observed annotation mix. */
export function defaultOperation(label) {
  return label === 'uncertain' ? 'keep_with_uncertainty_note' : 'remove';
}

/**
 * The action is DERIVED from the tag and what the annotator typed, not chosen from a
 * dropdown. This is the pipeline's own rule set (see prompts.py):
 *
 *   uncertain          -> always keep the text, carry a note
 *   wrong_uncertainty  -> always remove the wrong option; a replacement is never valid
 *   hallucination      -> remove by default; typing a replacement makes it a replace
 *
 * Deriving it removes a whole class of invalid combination - and a dropdown the
 * annotator had to reason about on every single tag.
 */
export function operationFor(label, { replacement_text = '', notes = '' } = {}) {
  if (label === 'uncertain') return 'keep_with_uncertainty_note';
  if (label === 'wrong_uncertainty') return 'remove';
  return String(replacement_text || '').trim() ? 'replace' : 'remove';
}

/** Does this tag type take a free-text field, and what is it called? */
export function fieldFor(label) {
  if (label === 'uncertain') {
    return {
      key: 'notes',
      label: 'What is unclear?',
      placeholder: 'e.g. material unclear likely metal',
      required: true,
    };
  }
  if (label === 'hallucinated') {
    return {
      key: 'replacement_text',
      label: 'Replace with',
      placeholder: 'leave empty to just remove it',
      required: false,
    };
  }
  return null;   // wrong_uncertainty: the hedge is simply removed
}

export const labelOf = (span) => span?.label ?? 'hallucinated';
export const operationOf = (span) => span?.operation || defaultOperation(labelOf(span));
export const replacementOf = (span) => String(span?.replacement_text ?? '').trim();
export const noteOf = (span) => String(span?.notes ?? '').trim();
export const classNameOf = (span) => TAG_META[labelOf(span)]?.className ?? '';

/** ` <note>` as it appears inside the derived final caption. */
export function noteSuffix(note) {
  const clean = String(note || '')
    .trim()
    .replace(/^<+|>+$/g, '')
    .trim();
  return clean ? ` <${clean}>` : '';
}

/** Sorted, index-stamped copy of a span list. Invalid spans are dropped rather than
 *  rendered at a nonsense position. */
export function collectSpans(spans) {
  return (spans || [])
    .map((span, index) => ({ ...span, index }))
    .filter(
      (s) =>
        Number.isInteger(s.start_char) &&
        Number.isInteger(s.end_char) &&
        s.end_char > s.start_char,
    )
    .sort((a, b) => a.start_char - b.start_char || a.end_char - b.end_char);
}

export function spansOverlap(a, b) {
  return a.start_char < b.end_char && b.start_char < a.end_char;
}

/** The existing span a new selection would collide with, if any. Overlapping tags are
 *  disallowed (inherited rule, and the backend rejects them too). */
export function findOverlap(spans, range) {
  return (spans || []).find((s) => spansOverlap(s, range)) || null;
}

/** Is this span ready to save? Mirrors the server's validation so the UI can warn
 *  before a 422 rather than after. */
export function spanProblem(span) {
  // With the action derived, a `replace` without replacement text is unreachable; an
  // uncertain tag with no note is the only way a span can be unfinished.
  if (labelOf(span) === 'uncertain' && !noteOf(span)) return 'Needs a note';
  return null;
}

export function summarise(spans) {
  const counts = { hallucinated: 0, uncertain: 0, wrong_uncertainty: 0 };
  for (const span of spans || []) {
    if (counts[span.label] !== undefined) counts[span.label] += 1;
  }
  return counts;
}
